import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {FlagLine, JiraExternalLogo, MetricsLine, StarLine} from '@dropbox/dig-icons/assets';
import {StatusButtonIcon} from 'components/DSYS/StatusButtonIcon';
import {Fragment, useState} from 'react';

import {FadeIn} from './FadeIn';
import {FoldToggle} from './FoldToggle';
import {currentQuarterLabel, type ProgressItem} from './scanProgressItems';
import {logWorkstreamsInteraction} from './workstreamsAnalytics';

// The detail page's type icons, so a row says what it is: star (north star),
// chart (metric), flag (milestone), Jira logo.
const TYPE_ICONS = {metric: MetricsLine, milestone: FlagLine, jira: JiraExternalLogo};

// "★ North star · Q4 · Lagging · Shahar Marom" / "⚑ Sep 30 · Kevin Benik" /
// "⚑ Completed Oct 14 · Kevin Benik"; an overdue date is the only colored part.
export const ProgressMeta = ({item}: {item: ProgressItem}) => {
  const parts: Array<{text: string; error?: boolean}> = [
    ...(item.isNorthStar ? [{text: 'North star'}, {text: currentQuarterLabel()}] : []),
    ...(item.metricTypeLabel ? [{text: item.metricTypeLabel}] : []),
    ...(item.closedLabel
      ? [{text: item.closedLabel}]
      : item.dueLabel
        ? [{text: item.dueLabel, error: item.overdue}]
        : []),
    ...(item.ownerName ? [{text: item.ownerName}] : []),
    ...(item.jiraKey ? [{text: item.jiraKey}] : []),
  ];
  return (
    <Box display="flex" alignItems="center" style={{gap: 4, minWidth: 0}}>
      <UIIcon
        src={item.isNorthStar ? StarLine : TYPE_ICONS[item.kind]}
        size="small"
        className={atoms({color: 'Text Subtle', flexShrink: 0})}
      />
      <Text size="xsmall" color="faint">
        {parts.map((part, index) => (
          <Fragment key={part.text}>
            {index > 0 && ' · '}
            {part.error ? (
              <Text size="xsmall" color="error">
                {part.text}
              </Text>
            ) : (
              part.text
            )}
          </Fragment>
        ))}
      </Text>
    </Box>
  );
};

// " · Q4" after a label over metric values: the quarter they're all measured
// against, said once instead of on every row.
export const QuarterSuffix = () => (
  <Text size="small" color="faint">
    {` · ${currentQuarterLabel()}`}
  </Text>
);

// Fits "$2.8M" over "of 444 goal", so titles start at the same x (and the
// north star's, in its own box, lines up with the Metrics rows below it).
const VALUE_COLUMN_PX = 88;

// A metric's number over its plan, in a fixed column right after the status, so
// the numbers read down as a column. `large`: the north star's.
export const MetricValue = ({value, large}: {value?: ProgressItem['value']; large?: boolean}) => (
  <Box style={{width: VALUE_COLUMN_PX, flexShrink: 0}}>
    <Text size={large ? 'large' : 'small'} isBold tagName="div">
      {value?.current ?? '-'}
    </Text>
    <Text size="xsmall" color="faint" tagName="div">
      {value?.plan
        ? `of ${value.plan} ${value.planLabel === 'budget' ? 'goal' : 'stretch goal'}`
        : 'No plan set'}
    </Text>
  </Box>
);

// [status]  [value]  Title        (metrics)
//           [plan ]  meta
// [status]  Title                 (milestones, initiatives)
//           meta
export const ProgressRow = ({item}: {item: ProgressItem}) => (
  <Box display="flex" alignItems="flex-start" style={{gap: 12, minWidth: 0}}>
    {/* 2px drops the 16px icon onto the first (20px) line. */}
    <Box style={{flexShrink: 0, paddingTop: 2}}>
      <StatusButtonIcon status={item.status} size="small" includeArchived />
    </Box>
    {item.kind === 'metric' && <MetricValue value={item.value} />}
    <Box style={{flex: 1, minWidth: 0}}>
      <Text size="small" color={item.closed ? 'subtle' : 'standard'}>
        <Truncate lines={2}>{item.title}</Truncate>
      </Text>
      <ProgressMeta item={item} />
    </Box>
  </Box>
);

// Rows, with a faint hairline between each so the eye can track a long title
// across, the same for every type so side-by-side columns match. (Not a hover
// tint: the rows aren't clickable.) Sits in a 12px-gap Stack.
export const DividedRows = ({items}: {items: ProgressItem[]}) => (
  <>
    {items.map((item, index) => (
      <Fragment key={item.key}>
        {index > 0 && <Box backgroundColor="Opacity Surface" style={{height: 1}} />}
        <ProgressRow item={item} />
      </Fragment>
    ))}
  </>
);

// "› More (4)" over the rows past the cap.
export const FoldedRows = ({label, items}: {label: string; items: ProgressItem[]}) => {
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;
  return (
    <Stack gap="12">
      <FoldToggle
        label={label}
        count={items.length}
        open={open}
        onToggle={() => {
          logWorkstreamsInteraction('fold_toggled', {
            fold: `progress_${label.toLowerCase()}`,
            expanded: !open,
          });
          setOpen(!open);
        }}
      />
      {open && (
        // Its own Stack, so the rows keep their gaps inside the fade.
        <FadeIn>
          <Stack gap="12">
            <DividedRows items={items} />
          </Stack>
        </FadeIn>
      )}
    </Stack>
  );
};
